import moment, { type Moment } from 'moment'
import { useCallback, useEffect, useState } from 'react'
import { useBoolean } from 'react-use'

import useAuth from 'hooks/use-auth'
import { type AttackTrigger } from 'types/api/schedules/active'
import {
  type RestrictedTimeWindowForDay,
  type RestrictedTimeWindows
} from 'types/api/teams'
import { type ScheduleDay } from 'types/schedules/schedules'
import { WEEK_DAYS } from 'utils/time/days'

type SerializedWindowForDay = {
  day: ScheduleDay
  window: RestrictedTimeWindowForDay
}

// pass in date time string & RestrictedTimeWindows[] list
// return isRestrictedTimeWindowActive and startDate and endDate if any match the specified time
export function checkForRestrictedTimeWindow(
  dateTime: string,
  restrictedTimeWindows: RestrictedTimeWindows[]
) {
  const targetDateTime = moment.utc(dateTime)
  const serializedWindowsForDay = serializeWindowsForDay(restrictedTimeWindows)
  const currentActiveRestrictedTimeWindow =
    getCurrentActiveRestrictedTimeWindow(
      targetDateTime,
      serializedWindowsForDay
    )

  if (currentActiveRestrictedTimeWindow) {
    const dayIndex = WEEK_DAYS.indexOf(currentActiveRestrictedTimeWindow.day)

    const startTime = moment
      .utc(currentActiveRestrictedTimeWindow.window.start, 'HH:mm')
      .day(dayIndex)
    const endTime = moment
      .utc(currentActiveRestrictedTimeWindow.window.end, 'HH:mm')
      .day(dayIndex)

    if (endTime.isBefore(startTime)) {
      endTime.add(1, 'day')
    }

    return {
      isRestrictedTimeWindowActive: true,
      windowStart: startTime.toDate(),
      windowEnd: endTime.toDate()
    }
  }

  return {
    isRestrictedTimeWindowActive: false,
    windowStart: null,
    windowEnd: null
  }
}

function serializeWindowsForDay(
  restrictedTimeWindows: RestrictedTimeWindows[]
): SerializedWindowForDay[] {
  return restrictedTimeWindows?.flatMap((restrictedTimeWindow) =>
    restrictedTimeWindow.windows_for_day.map((windowForDay) => ({
      day: restrictedTimeWindow.day_of_week,
      window: windowForDay
    }))
  )
}

function getMatchingWindowForAttackTrigger(
  attackTrigger: AttackTrigger,
  serializedWindowsForDay: SerializedWindowForDay[]
): SerializedWindowForDay | null {
  return (
    serializedWindowsForDay?.find((windowForDay) => {
      const { day, window } = windowForDay
      const dayIndex = WEEK_DAYS.indexOf(day)

      // build UTC start and end times for the window
      const windowStart = moment.utc(window.start, 'HH:mm').day(dayIndex)
      const windowEnd = moment.utc(window.end, 'HH:mm').day(dayIndex)

      if (windowEnd.isBefore(windowStart)) {
        windowEnd.add(1, 'day')
      }

      // Loop through the activeDays list of the attackTrigger and find the first day that falls within a restricted time window
      const attackTriggerDay = attackTrigger.activeDays.find((activeDay) => {
        const attackTriggerDayIndex = WEEK_DAYS.indexOf(activeDay)

        // build UTC start and end times for attackTrigger and convert from its timeZone
        const attackTriggerStart = moment(attackTrigger.start, 'HH:mm')
          .tz(attackTrigger.timeZone ?? 'UTC')
          .day(attackTriggerDayIndex)
        const attackTriggerEnd = moment(attackTrigger.end, 'HH:mm')
          .tz(attackTrigger.timeZone ?? 'UTC')
          .day(attackTriggerDayIndex)

        if (attackTriggerEnd.isBefore(attackTriggerStart)) {
          attackTriggerEnd.add(1, 'day')
        }

        // if it starts or ends within this window
        if (
          attackTriggerStart.isBetween(
            windowStart,
            windowEnd,
            undefined,
            '[]'
          ) ||
          attackTriggerEnd.isBetween(windowStart, windowEnd, undefined, '[]')
        ) {
          return true
        }

        return false
      })

      return Boolean(attackTriggerDay)
    }) || null
  )
}

function getCurrentActiveRestrictedTimeWindow(
  targetDateTime: Moment,
  serializedWindowsForDay: SerializedWindowForDay[]
): SerializedWindowForDay | null {
  return (
    serializedWindowsForDay?.find((windowForDay) => {
      const { day, window } = windowForDay
      const dayIndex = WEEK_DAYS.indexOf(day)

      const startTime = moment.utc(window.start, 'HH:mm').day(dayIndex)
      const endTime = moment.utc(window.end, 'HH:mm').day(dayIndex)

      if (endTime.isBefore(startTime)) {
        endTime.add(1, 'day')
      }

      return targetDateTime.isBetween(startTime, endTime, undefined, '[]')
    }) || null
  )
}

export function useRestrictedTimeWindow() {
  const { currentTeam } = useAuth()

  const [isRestrictedTimeWindowActive, setIsRestrictedTimeWindowActive] =
    useBoolean(false)
  const [windowStart, setWindowStart] = useState<Moment | null>(null)
  const [windowEnd, setWindowEnd] = useState<Moment | null>(null)

  const restrictedTimeWindows = currentTeam?.prefs?.test_run_restriction_windows

  useEffect(() => {
    const timeWindow = checkForRestrictedTimeWindow(
      moment.utc().toISOString(),
      restrictedTimeWindows
    )

    if (
      isRestrictedTimeWindowActive !== timeWindow.isRestrictedTimeWindowActive
    ) {
      setIsRestrictedTimeWindowActive(timeWindow.isRestrictedTimeWindowActive)
    }
    setWindowEnd(
      timeWindow.windowEnd && moment.utc(timeWindow.windowEnd, 'HH:mm')
    )
    setWindowStart(
      timeWindow.windowStart && moment.utc(timeWindow.windowStart, 'HH:mm')
    )
  }, [
    isRestrictedTimeWindowActive,
    restrictedTimeWindows,
    setIsRestrictedTimeWindowActive
  ])

  const checkForRestrictedTimeWindowMemoized = useCallback(
    (dateTime: string) => {
      return checkForRestrictedTimeWindow(dateTime, restrictedTimeWindows)
    },
    [restrictedTimeWindows]
  )

  const checkForRestrictedTimeWindowForAttackTrigger = useCallback(
    (attackTrigger: AttackTrigger) => {
      const serializedWindowsForDay = serializeWindowsForDay(
        restrictedTimeWindows
      )

      return getMatchingWindowForAttackTrigger(
        attackTrigger,
        serializedWindowsForDay
      )
    },
    [restrictedTimeWindows]
  )

  return {
    isRestrictedTimeWindowActive,
    windowStart,
    windowEnd,
    checkForRestrictedTimeWindow: checkForRestrictedTimeWindowMemoized,
    checkAttackTrigger: checkForRestrictedTimeWindowForAttackTrigger
  }
}
